import React, { useState, useEffect } from 'react';
import './SessionDayModal.css';

interface SessionDayEntry {
  color: string | null;
  label: string | null;
  real_date: string | null;
  hours: number;
}

interface SessionDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (color: string, label: string, realDate?: string, hours?: number) => void;
  onRemoveEntry?: (day: number, realDate: string) => void;
  currentSession: string;
  year: number;
  month: number;
  day: number;
  existingSessionDay?: SessionDayEntry | null;
  existingEntries?: SessionDayEntry[];
}

const SessionDayModal: React.FC<SessionDayModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onRemoveEntry,
  currentSession,
  year,
  month,
  day,
  existingSessionDay,
  existingEntries = []
}) => {
  const [selectedColor, setSelectedColor] = useState<string>('#ff6b6b');
  const [label, setLabel] = useState<string>('');
  const [realDate, setRealDate] = useState<string>('');
  const [hours, setHours] = useState<number>(24);
  const [customHours, setCustomHours] = useState<boolean>(false);

  const predefinedColors = [
    '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7',
    '#dda0dd', '#ffa07a', '#87ceeb', '#98d8c8', '#f7dc6f'
  ];

  const generateRandomColor = () => {
    return predefinedColors[Math.floor(Math.random() * predefinedColors.length)];
  };

  useEffect(() => {
    if (existingSessionDay) {
      setSelectedColor(existingSessionDay.color || generateRandomColor());
      setLabel(existingSessionDay.label || `Session ${currentSession}`);
      setRealDate(existingSessionDay.real_date || new Date().toISOString().split('T')[0]);
      setHours(existingSessionDay.hours || 24);
      setCustomHours(true);
    } else {
      setSelectedColor(generateRandomColor());
      setLabel(`Session ${currentSession}`);
      setRealDate(new Date().toISOString().split('T')[0]);
      // Default: if existing entries, auto-split; otherwise 24
      const newCount = existingEntries.length + 1;
      setHours(24 / newCount);
      setCustomHours(false);
    }
  }, [isOpen, existingSessionDay, currentSession, existingEntries.length]);

  const handleConfirm = () => {
    onConfirm(
      selectedColor,
      label.trim() || `Session ${currentSession}`,
      realDate || undefined,
      customHours ? hours : undefined
    );
    onClose();
  };

  const handleRandomColor = () => {
    setSelectedColor(generateRandomColor());
  };

  const totalExistingHours = existingEntries.reduce((sum, e) => sum + (e.hours || 0), 0);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay session-day-modal-overlay">
      <div className="modal-content session-day-modal">
        <div className="modal-header">
          <h3>Session Tag markieren</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="session-day-form">
          <div className="date-info">
            <p><strong>Kalendertag:</strong> {day}. Monat {month + 1}, Jahr {year}</p>
          </div>

          {/* Existing entries for this day */}
          {existingEntries.length > 0 && (
            <div className="existing-entries">
              <label>Bestehende Sessions an diesem Tag:</label>
              <div className="entries-list">
                {existingEntries.map((entry, idx) => (
                  <div key={idx} className="entry-item" style={{ borderLeft: `4px solid ${entry.color || '#ccc'}` }}>
                    <div className="entry-info">
                      <span className="entry-label">{entry.label || 'Session'}</span>
                      <span className="entry-date">{entry.real_date || '—'}</span>
                      <span className="entry-hours">{(entry.hours || 0).toFixed(1)}h</span>
                    </div>
                    {onRemoveEntry && entry.real_date && (
                      <button
                        className="entry-remove-btn"
                        onClick={() => onRemoveEntry(day, entry.real_date!)}
                        title="Eintrag entfernen"
                      >&times;</button>
                    )}
                  </div>
                ))}
              </div>
              <div className="total-hours-info">
                Gesamt: {totalExistingHours.toFixed(1)}h / 24h
                <div className="total-progress-bar">
                  <div className="total-progress-fill" style={{ width: `${Math.min((totalExistingHours / 24) * 100, 100)}%` }} />
                </div>
              </div>
            </div>
          )}

          {/* Real-world date picker */}
          <div className="real-date-input">
            <label htmlFor="session-real-date">Spieltag (IRL Datum):</label>
            <input
              id="session-real-date"
              type="date"
              value={realDate}
              onChange={(e) => setRealDate(e.target.value)}
            />
          </div>

          {/* Hours input */}
          <div className="hours-input">
            <label>
              <input
                type="checkbox"
                checked={customHours}
                onChange={(e) => {
                  setCustomHours(e.target.checked);
                  if (!e.target.checked) {
                    setHours(24 / (existingEntries.length + 1));
                  }
                }}
              />
              Stunden manuell festlegen
            </label>
            {customHours ? (
              <div className="hours-slider">
                <input
                  type="range"
                  min="0.5"
                  max="24"
                  step="0.5"
                  value={hours}
                  onChange={(e) => setHours(parseFloat(e.target.value))}
                />
                <input
                  type="number"
                  min="0.5"
                  max="24"
                  step="0.5"
                  value={hours}
                  onChange={(e) => setHours(Math.max(0.5, Math.min(24, parseFloat(e.target.value) || 0.5)))}
                  className="hours-number-input"
                />
                <span className="hours-unit">Stunden</span>
              </div>
            ) : (
              <div className="hours-auto-info">
                Automatisch: {(24 / (existingEntries.length + (existingSessionDay ? 0 : 1))).toFixed(1)}h
                ({existingEntries.length + (existingSessionDay ? 0 : 1)} Session{existingEntries.length + (existingSessionDay ? 0 : 1) !== 1 ? 's' : ''} = 24h / {existingEntries.length + (existingSessionDay ? 0 : 1)})
              </div>
            )}
          </div>

          <div className="color-selection">
            <label>Farbe:</label>
            <div className="color-grid">
              {predefinedColors.map((color) => (
                <button
                  key={color}
                  className={`color-option ${selectedColor === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setSelectedColor(color)}
                  title={color}
                />
              ))}
            </div>
            <button className="random-color-btn" onClick={handleRandomColor}>
              Zufällige Farbe
            </button>
          </div>

          <div className="label-input">
            <label htmlFor="session-label">Label:</label>
            <input
              id="session-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={`Session ${currentSession}`}
              maxLength={50}
            />
          </div>

          <div className="preview">
            <div className="day-preview" style={{ backgroundColor: selectedColor }}>
              <span className="preview-label">{label || `Session ${currentSession}`}</span>
              <span className="preview-date">{realDate}</span>
              <span className="preview-hours">{(customHours ? hours : 24 / (existingEntries.length + 1)).toFixed(1)}h</span>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={handleConfirm}>
            {existingSessionDay ? 'Aktualisieren' : 'Markieren'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionDayModal;
